import { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import Layout from "../../components/Layout";
import api from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { Lock, Sparkles, ArrowLeft, EyeOff, MapPin, ShieldCheck, FileSignature, BookOpen } from "lucide-react";
import { DataRoomCard } from "../../components/dataroom/DataRoomCard";
import { toast } from "sonner";
import TeaserMarkdown from "../../components/TeaserMarkdown";
import { t } from "../../lib/i18n";

export default function CompanyDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [company, setCompany] = useState(null);
  const [interest, setInterest] = useState(null);
  const [message, setMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [ndaSigned, setNdaSigned] = useState(false);
  const [ndaSig, setNdaSig] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const r = await api.get(`/companies/${id}`);
        setCompany(r.data);
      } catch (e) {
        toast.error(t("Empresa no disponible"));
      }
      try {
        const mine = await api.get("/interests/mine");
        const existing = (mine.data || []).find((it) => it.company_id === id);
        setInterest(existing || null);
      } catch {}
      try {
        const s = await api.get(`/nda/status?company_id=${id}`);
        setNdaSigned(Boolean(s.data.signed));
        setNdaSig(s.data.signature || null);
      } catch {}
    })();
  }, [id]);

  const expressInterest = async () => {
    setLoading(true);
    try {
      const r = await api.post("/interests", { company_id: id, message: message.trim() || null });
      setInterest(r.data);
      toast.success(t("Interés registrado · el vendedor recibirá tu mensaje"));
    } catch (e) {
      toast.error(t("Error registrando interés"));
    } finally {
      setLoading(false);
    }
  };

  if (!company) {
    return (
      <Layout>
        <div className="max-w-[1200px] mx-auto px-6 py-16">
          <span className="font-mono-label text-muted-foreground">{t("Cargando…")}</span>
        </div>
      </Layout>
    );
  }

  const facts = company.public_facts || {};
  const fin = company.financial_signal || {};

  return (
    <Layout>
      <div className="max-w-[1200px] mx-auto px-6 lg:px-10 py-12 overflow-x-hidden">
        <Link to="/searcher/marketplace" className="font-mono-label text-muted-foreground inline-flex items-center gap-2 mb-8 hover:text-foreground" data-testid="back-to-market">
          <ArrowLeft className="w-3 h-3" />{" "}{t("Volver al marketplace")}
        </Link>

        <div className="grid lg:grid-cols-12 gap-10">
          <div className="lg:col-span-8">
            <div className="flex items-center gap-2 mb-2">
              <span className="font-mono-label text-gold">{company.sector}</span>
              <span className="font-mono-label text-muted-foreground">·</span>
              <span className="font-mono-label text-muted-foreground inline-flex items-center gap-1">
                <MapPin className="w-3 h-3" /> {company.country}
              </span>
              <span className="font-mono-label text-muted-foreground">·</span>
              <span className="font-mono-label text-muted-foreground inline-flex items-center gap-1">
                <EyeOff className="w-3 h-3" />{" "}{t("Teaser ciego")}
              </span>
            </div>
            <h1 className="font-serif-display text-5xl md:text-6xl mt-1 leading-tight">{company.name_blind || company.sector}</h1>

            {company.score != null && (
              <div className="mt-6 hairline inline-flex items-center gap-4 px-5 py-3 bg-bone">
                <Sparkles className="w-4 h-4 text-gold" />
                <div>
                  <div className="font-mono-label text-gold">{t("Análisis IA")}</div>
                  <div className="text-sm text-muted-foreground max-w-lg">{company.score_rationale}</div>
                </div>
                <div className="hairline pl-5 ml-2">
                  <div className="font-serif-display text-3xl text-gold leading-none">{company.score}</div>
                  <div className="font-mono-label text-muted-foreground text-center" style={{ fontSize: "0.55rem" }}>/100</div>
                </div>
              </div>
            )}

            {/* Public facts grid */}
            <div className="mt-10 hairline grid grid-cols-2 md:grid-cols-4 bg-card">
              <Fact label={t("Empleados")} value={facts.employees ?? "—"} />
              <Fact label={t("Año fundación")} value={facts.year_founded ?? "—"} border />
              <Fact label={t("Margen EBITDA")} value={fin.ebitda_margin_pct != null ? `${fin.ebitda_margin_pct}%` : "—"} border />
              <Fact label={t("Crecimiento anual")} value={fin.growth_pct != null ? `${fin.growth_pct}%` : "—"} border />
            </div>

            {/* TEASER */}
            <div className="mt-10 hairline bg-card">
              <div className="px-7 py-4 hairline-b bg-[#1E3A5F] text-bone flex items-center gap-3">
                <EyeOff className="w-4 h-4" />
                <span className="font-mono-label text-gold">{t("Teaser ciego · publicado por el vendedor")}</span>
              </div>
              <div className="p-8 lg:p-10" data-testid="searcher-teaser-content">
                {company.teaser_text ? (
                  <TeaserMarkdown text={company.teaser_text} />
                ) : (
                  <p className="text-sm text-muted-foreground italic">{t("El vendedor aún no ha publicado el teaser.")}</p>
                )}
              </div>
            </div>

            {/* Sealed section */}
            <div className="mt-10 hairline bg-bone p-7">
              <div className="flex items-start gap-4">
                <Lock className="w-5 h-5 mt-1" />
                <div>
                  <h3 className="font-serif-display text-2xl">{t("Información detallada bajo NDA")}</h3>
                  <p className="text-sm text-muted-foreground mt-1 max-w-xl">
                    {t("Nombre, datos financieros completos, equipo directivo, clientes y due diligence se desbloquean cuando el vendedor acepte tu interés y firméis el NDA en plataforma.")}
                  </p>
                </div>
              </div>
            </div>
          </div>

          <aside className="lg:col-span-4">
            <div className="hairline bg-card sticky top-20">
              <div className="px-6 py-5 hairline-b bg-[#1E3A5F] text-bone">
                <span className="font-mono-label text-gold">{t("Acciones")}</span>
                <h3 className="font-serif-display text-2xl mt-1">{t("Próximo paso")}</h3>
              </div>
              <div className="p-6 space-y-5">
                {interest ? (
                  <div className="hairline p-4 bg-bone">
                    <span className="font-mono-label text-forest">{t("Interés registrado")}</span>
                    <p className="text-sm mt-1">{t("Estado actual:")}{" "}<span className="font-medium uppercase">{interest.status}</span></p>
                    {interest.message && (
                      <p className="text-xs text-muted-foreground italic mt-2" data-testid="interest-message-sent">"{interest.message}"</p>
                    )}
                    <Link to="/searcher/pipeline" className="font-mono-label text-gold mt-3 inline-flex items-center gap-1" data-testid="detail-go-pipeline">
                      {t("Ir al pipeline →")}
                    </Link>
                  </div>
                ) : (
                  <div className="space-y-3">
                    <Textarea
                      value={message}
                      onChange={(e) => setMessage(e.target.value)}
                      rows={4}
                      maxLength={500}
                      placeholder={t("Mensaje para el vendedor (opcional): quién eres, por qué te interesa esta empresa y qué te gustaría saber…")}
                      className="rounded-none text-sm"
                      data-testid="interest-message"
                    />
                    <Button
                      onClick={expressInterest}
                      disabled={loading}
                      className="rounded-none w-full bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-12"
                      data-testid="express-interest-btn"
                    >
                      {loading ? t("Registrando…") : t("Expresar interés")}
                    </Button>
                  </div>
                )}

                {ndaSigned ? (
                  <div className="hairline p-4 bg-forest/5 flex items-start gap-3" data-testid="nda-signed-badge">
                    <ShieldCheck className="w-5 h-5 text-forest flex-shrink-0 mt-0.5" />
                    <div className="flex-1 min-w-0">
                      <span className="font-mono-label text-forest">{t("NDA firmado")}</span>
                      {ndaSig?.seller_approval === "approved" ? (
                        company.im_published ? (
                          <Button
                            onClick={() => navigate(`/im/${id}`)}
                            className="rounded-none w-full mt-3 bg-[#1E3A5F] hover:bg-[#2B4C78] text-bone h-auto min-h-11 py-2.5 px-4 text-sm whitespace-normal leading-tight"
                            data-testid="view-im-btn"
                          >
                            <BookOpen className="w-4 h-4 mr-2 flex-shrink-0" />{" "}{t("Ver Information Memorandum")}
                          </Button>
                        ) : (
                          <p className="text-sm mt-1 text-muted-foreground" data-testid="im-not-ready">
                            {t("NDA aprobado por el vendedor. Está preparando el Information Memorandum.")}
                          </p>
                        )
                      ) : ndaSig?.seller_approval === "rejected" ? (
                        <p className="text-sm mt-1 text-destructive" data-testid="im-access-rejected">
                          {t("El vendedor ha declinado tu solicitud de acceso a más información.")}
                        </p>
                      ) : (
                        <p className="text-sm mt-1 text-muted-foreground" data-testid="im-pending-approval">
                          {t("Pendiente de aprobación del vendedor para acceder al Information Memorandum.")}
                        </p>
                      )}
                      <button
                        type="button"
                        onClick={() => navigate(`/nda/${id}`)}
                        className="font-mono-label text-gold mt-2 inline-flex items-center gap-1"
                        data-testid="view-nda-btn"
                      >
                        {t("Ver / descargar NDA →")}
                      </button>
                    </div>
                  </div>
                ) : (
                  <Button
                    onClick={() => navigate(`/nda/${id}`)}
                    variant="outline"
                    className="rounded-none w-full h-12 hairline"
                    data-testid="sign-nda-btn"
                  >
                    <FileSignature className="w-4 h-4 mr-2" />{" "}{t("Firmar NDA para desbloquear")}
                  </Button>
                )}
                <div className="hairline-t pt-5 space-y-2 text-sm">
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("Etapas completadas")}</span><span>{company.completed_stages?.length || 0}/7</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("Cualificada")}</span><span>{company.qualified ? t("Sí") : "No"}</span></div>
                  <div className="flex justify-between"><span className="text-muted-foreground">{t("Teaser")}</span><span>{company.teaser_published ? t("Publicado") : t("Borrador")}</span></div>
                </div>
              </div>
            </div>
          </aside>
        </div>

        {ndaSig?.seller_approval === "approved" && (
          <div className="mt-10">
            <DataRoomCard companyId={id} stage={company?.stage} />
          </div>
        )}
      </div>
    </Layout>
  );
}

const Fact = ({ label, value, border }) => (
  <div className={`p-5 ${border ? "hairline border-r-0 border-t-0 border-b-0" : ""}`}>
    <div className="font-mono-label text-muted-foreground" style={{ fontSize: "0.55rem" }}>{label}</div>
    <div className="font-serif-display text-2xl mt-1">{value}</div>
  </div>
);
